<template src="./ComplianceManager.html"></template>

<script>
import { OnlyAdmin, AdminCompliance } from '../../../utils/roles'
import { mapGetters } from 'vuex'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { getUsersCompliance, getComplianceByCompany, getAnnualComplianceReport } from '../../../services'
import { formatToTwoDecimals } from '../../../utils/computeds'

export default {
  name: 'ComplianceManager',
  data () {
    return {
      usersData: [],
      companiesData: [],
      sortOrder: 'ASC',
      sortField: 'hoursExecutedWeekly',
      showExtraColumns: false,
      selectedYear: new Date().getFullYear(),
      downloadingReport: false
    }
  },
  computed: {
    ...mapGetters({
      user: 'getUser'
    }),
    OnlyAdmin,
    AdminCompliance,
    reportYears () {
      const firstYear = 2024
      const lastYear = new Date().getFullYear() + 1
      return Array.from({ length: lastYear - firstYear + 1 }, (_, i) => lastYear - i)
    }
  },
  created () {
    this.fetchUsersData()
    this.fetchCompaniesData()
  },
  methods: {
    async fetchUsersData () {
      try {
        const response = await getUsersCompliance()
        this.usersData = response
      } catch (error) {
        console.error('Error fetching users data:', error)
      }
    },
    toggleSortOrder (field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortOrder = 'asc'
      }
      this.sortCompaniesData()
    },
    async fetchCompaniesData () {
      try {
        const response = await getComplianceByCompany()
        this.companiesData = response
      } catch (error) {
        console.error(error)
      }
    },
    sortCompaniesData () {
      this.companiesData.sort((a, b) => {
        const valueA = a[this.sortField]
        const valueB = b[this.sortField]

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return this.sortOrder === 'asc' ? valueA - valueB : valueB - valueA
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return this.sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
        }

        return 0
      })
    },
    async downloadAnnualReport () {
      if (this.downloadingReport) return
      this.downloadingReport = true
      const year = this.selectedYear
      try {
        const response = await getAnnualComplianceReport(year)
        const tasks = response.data || []
        if (tasks.length === 0) {
          this.$swal({ text: 'No hay actividades registradas para ese año.', buttons: false, icon: 'info', timer: 3000 })
          return
        }
        const statusLabels = { PLANNED: 'Planeada', IN_PROGRESS: 'En progreso', EXECUTED: 'Ejecutada' }
        const text = value => (value === null || value === undefined ? '' : value)
        const rows = tasks.map(task => ({
          Empresa: text(task.companyName),
          Analista: [task.userName, task.userLastName].filter(Boolean).join(' '),
          Estado: statusLabels[task.status] || text(task.status),
          Tipo: task.parentTaskId ? 'Subactividad' : 'Actividad',
          'Actividad padre': text(task.parentActivity),
          Actividad: text(task.activity),
          Guía: text(task.guide),
          'Fecha planeada': text(task.plannedDate),
          'Fecha ejecución': text(task.dateExecution),
          'Semana ejecución': text(task.weekExecution),
          'Horas asignadas': Number(task.assignedHours) || 0,
          'Horas ejecutadas': Number(task.timeDestined) || 0,
          'En sitio': task.onSite ? 'Sí' : 'No',
          'Documento asociado': text(task.documentAssociated),
          'Plan de cumplimiento': task.planTask ? 'Sí' : 'No',
          Observación: text(task.observation),
          Año: text(task.currentYear)
        }))
        const worksheet = XLSX.utils.json_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Actividades')
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
        saveAs(blob, `Informe cumplimiento ${year}.xlsx`)
      } catch (error) {
        console.error('Error generating annual compliance report:', error)
        this.$swal({ text: 'Ocurrió un error al generar el informe, intenta más tarde.', buttons: false, icon: 'error', timer: 3000 })
      } finally {
        this.downloadingReport = false
      }
    },
    formatToTwoDecimals,
    isZero (value) {
      return Number(value) === 0
    },
    isOverThreshold (value) {
      return Number(value) >= 50
    }
  }
}
</script>

<style lang="scss" src="./ComplianceManager.scss" scoped></style>
